import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { t } from 'src/i18n';
import { PageContent } from 'src/pages/page-content.js';
import 'src/utilities/display-alert';
import '../../content/d-entity-header.js';
import '../../library/components/d-help-section.js';
import '../../library/elements/d-action.js';
import type { PageViewModel } from '@trinnvis/dabih-api-client';
import { DNewContactAndPartnerDialog } from 'src/layout/parts/d-new-contact-and-partner-dialog';
import { uuid } from 'src/utilities/text';
import { features } from '../../store/selectors/features/features';
import type { PeoplePageViewModel } from './people-page-view-model';
import type { CreateEntityInput } from '../../layout/parts/create-entity-input';
import type { NewContactAndPartnerDialogResult } from '../../layout/parts/new-contact-and-partner-dialog-result';
import type { ActionInput } from '../../library/elements/action-input';

/**
 *
 */
@customElement('d-people-page-content')
export class DPeoplePageContent extends PageContent<PeoplePageViewModel> {
  static readonly styles = [
    ...super.styles,
    css`
      d-list-section {
        margin-bottom: 6px;
      }
    `,
  ];

  @property({ type: Array })
  pages: PageViewModel[] = [];
  @property({ type: Boolean, attribute: 'write-access', reflect: true })
  writeAccess = false;
  @property({ type: Boolean })
  editMode = false;
  @property({ type: Object })
  token = {};
  @property({ type: Boolean })
  uncoverPageMenu = false;
  @property({ type: String })
  key = '';

  private get employeeActions(): ActionInput[] {
    if (this.writeAccess) {
      return [
        {
          name: t('people.addEmployee'),
          action: 'create-employee',
          slot: 'top-right',
        },
      ];
    }
    return [];
  }

  private get partnerAndContactActions(): ActionInput[] {
    if (this.writeAccess) {
      return [
        {
          name: t('people.addPartner'),
          action: 'create-partner',
        },
        {
          name: t('people.addContact'),
          action: 'create-contact',
        },
      ];
    }
    return [];
  }

  renderContent() {
    if (this.pageView.promoData) {
      return html` <d-promo-section
        theme-page
        .employeesCount=${this.pageView.employeesCount}
        .specialTerms=${this.pageView.specialTerms}
        .promoData=${this.pageView.promoData}
        .features=${features}
      ></d-promo-section>`;
    }
    return html`
      <d-list-section
        theme-page
        .addItemText=${t('people.addPerson')}
        .items=${this.pageView.employees}
        icon="employees"
        label=${t('people.staffLabel')}
        .contentStickyTop=${this.contentStickyTop}
        .actions=${this.employeeActions}
        @action=${this.onAction}
      >
      </d-list-section>
      <d-list-section
        theme-page
        .items=${this.pageView.partnersAndContacts}
        icon="employees"
        label=${t('people.partnersLabel')}
        sublabel=${t('people.partnersSublabel')}
        .contentStickyTop=${this.contentStickyTop}
        .topRightMoreActions=${this.partnerAndContactActions}
        .topRightMoreActionsPrompt=${t('people.addPrompt')}
        @action=${this.onAction}
      >
      </d-list-section>
    `;
  }

  private async onAction(e) {
    e.stopPropagation();
    console.log('onAction', e.detail);
    if (e.detail === 'create-contact') {
      const result: NewContactAndPartnerDialogResult = await DNewContactAndPartnerDialog.open({
        userEmails: this.pageView.userEmails,
        partners: this.pageView.partners,
      });
      return result.action;
    }
    if (e.detail === 'create-employee') {
      const id = uuid();
      this.dispatchEvent(
        new CustomEvent<CreateEntityInput>('create-entity', {
          bubbles: true,
          composed: true,
          detail: {
            entityType: 'employees',
            entityUuid: id,
            targetUrl: this.pageView.href + 'employees/' + id + '?edit',
          },
        }),
      );
    }
    if (e.detail === 'create-partner') {
      console.log('onAction', 'PARTNER');
      const id = uuid();
      this.dispatchEvent(
        new CustomEvent<CreateEntityInput>('create-entity', {
          bubbles: true,
          composed: true,
          detail: {
            entityType: 'partners',
            entityUuid: id,
            pageId: 357, // TODO: skal ikke trenge noen sideId
            targetUrl: this.pageView.href + 'partners/' + id + '?edit',
          },
        }),
      );
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-people-page-content': DPeoplePageContent;
  }
}
